import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Button } from '@/components/ui/button'
import { BlockedActionButton } from '@/components/create-monitor-block'
import { useCreateMonitorBlock } from '@/hooks/use-create-monitor-block'
import { Card, CardContent, CardDescription, CardHeader } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { apiV2 } from '@/lib/api-v2-client'
import { toast } from 'sonner'
import { ArrowLeft, Upload, FileUp } from 'lucide-react'

export const Route = createFileRoute('/_authenticated/dashboard/monitors/import')({
  component: ImportPage,
})

type ParsedRow = { url: string; name?: string; type?: string; interval?: number; keyword?: string }

// ── Minimal CSV parser (handles quoted fields + embedded commas/quotes) ──
function parseCSV(text: string): string[][] {
  const rows: string[][] = []
  let field = ''
  let row: string[] = []
  let inQuotes = false
  for (let i = 0; i < text.length; i++) {
    const c = text[i]
    if (inQuotes) {
      if (c === '"') {
        if (text[i + 1] === '"') { field += '"'; i++ } else { inQuotes = false }
      } else field += c
    } else if (c === '"') inQuotes = true
    else if (c === ',') { row.push(field); field = '' }
    else if (c === '\n' || c === '\r') {
      if (c === '\r' && text[i + 1] === '\n') i++
      row.push(field); field = ''
      if (row.some((f) => f.trim() !== '')) rows.push(row)
      row = []
    } else field += c
  }
  if (field !== '' || row.length) { row.push(field); if (row.some((f) => f.trim() !== '')) rows.push(row) }
  return rows
}

// Find the column index whose header matches any of the given synonyms.
function colIndex(headers: string[], synonyms: string[]): number {
  const norm = (s: string) => s.trim().toLowerCase().replace(/[^a-z0-9]/g, '')
  const h = headers.map(norm)
  for (const syn of synonyms) {
    const idx = h.indexOf(norm(syn))
    if (idx !== -1) return idx
  }
  return -1
}

// Map a parsed CSV (UptimeRobot / Pingdom / generic) into normalized rows.
function mapRows(rows: string[][]): { rows: ParsedRow[]; source: string } {
  if (rows.length < 2) return { rows: [], source: 'generic' }
  const headers = rows[0]
  const urlI = colIndex(headers, ['url', 'url/ip', 'urlip', 'hostname', 'host', 'target', 'address'])
  const nameI = colIndex(headers, ['friendly name', 'name', 'check name', 'monitor name', 'label'])
  const typeI = colIndex(headers, ['type', 'monitor type', 'check type'])
  const intervalI = colIndex(headers, ['interval', 'monitoring interval', 'interval (seconds)', 'resolution', 'frequency', 'check interval'])
  const keywordI = colIndex(headers, ['keyword', 'keyword value', 'search string'])

  // Source detection (informational).
  const hj = headers.join(' ').toLowerCase()
  const source = hj.includes('friendly name') ? 'uptimerobot' : hj.includes('check name') || hj.includes('resolution') ? 'pingdom' : 'generic'

  // The unit of the interval column. Pingdom "Resolution" is in minutes, UptimeRobot "Interval"
  // is in seconds, and a header can say it. Only a file that does not say (a generic CSV) is
  // guessed: a small number (up to 10) is minutes, anything else seconds. Seconds is the safer
  // guess: minutes read as seconds only checks more often, but seconds read as minutes turned
  // a 60-second monitor into an hourly one.
  const intervalHeader = intervalI >= 0 ? headers[intervalI].toLowerCase() : ''
  const unit: 'seconds' | 'minutes' | 'auto' = intervalHeader.includes('second')
    ? 'seconds'
    : intervalHeader.includes('minute') || intervalHeader.includes('resolution') || source === 'pingdom'
      ? 'minutes'
      : source === 'uptimerobot'
        ? 'seconds'
        : 'auto'

  const out: ParsedRow[] = []
  for (const r of rows.slice(1)) {
    const url = (urlI >= 0 ? r[urlI] : r[0] || '').trim()
    if (!url) continue
    const rawInterval = intervalI >= 0 ? r[intervalI]?.trim() : ''
    let interval = rawInterval ? parseInt(rawInterval.replace(/[^0-9]/g, ''), 10) : undefined
    if (interval !== undefined && !Number.isNaN(interval) && interval > 0) {
      if (unit === 'minutes' || (unit === 'auto' && interval <= 10)) interval = interval * 60
    }
    if (interval !== undefined && Number.isNaN(interval)) interval = undefined
    out.push({
      url,
      name: nameI >= 0 ? r[nameI]?.trim() || undefined : undefined,
      type: typeI >= 0 ? r[typeI]?.trim() || undefined : undefined,
      interval,
      keyword: keywordI >= 0 ? r[keywordI]?.trim() || undefined : undefined,
    })
  }
  return { rows: out, source }
}

function ImportPage() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const createBlock = useCreateMonitorBlock()
  const [parsed, setParsed] = useState<ParsedRow[]>([])
  const [source, setSource] = useState('generic')
  const [fileName, setFileName] = useState('')
  const [result, setResult] = useState<Awaited<ReturnType<typeof apiV2.importMonitors>> | null>(null)

  const importMutation = useMutation({
    mutationFn: () => apiV2.importMonitors(source, parsed.map((p) => ({ ...p, url: p.url }))),
    onSuccess: (res) => {
      setResult(res)
      queryClient.invalidateQueries({ queryKey: ['monitors'] })
      queryClient.invalidateQueries({ queryKey: ['dashboard-stats'] })
      if (res.created > 0) {
        toast.success(`Imported ${res.created} monitor${res.created === 1 ? '' : 's'}`)
      } else {
        // Nothing was created (plan limit, a type the plan does not include, duplicates).
        // The table below names the reason for each row.
        toast.error('No monitors were imported. See the reasons below.')
      }
    },
    onError: (e: unknown) => toast.error(e instanceof Error ? e.message : 'Import failed'),
  })

  function handleFile(file: File) {
    setFileName(file.name)
    setResult(null)
    const reader = new FileReader()
    reader.onload = () => {
      const text = String(reader.result ?? '')
      const { rows, source } = mapRows(parseCSV(text))
      setParsed(rows)
      setSource(source)
      if (rows.length === 0) toast.error('No rows with a URL found in that file')
    }
    reader.readAsText(file)
  }

  return (
    <div className="space-y-4 max-w-3xl">
      <Link to="/dashboard/monitors" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground">
        <ArrowLeft className="h-4 w-4" /> Back to monitors
      </Link>

      <Card>
        <CardHeader>
          <h1 data-slot="card-title" className="leading-none font-semibold">Import monitors</h1>
          <CardDescription>
            Switching from UptimeRobot, Pingdom, or another tool? Export your monitors to CSV and drop it here —
            we&apos;ll recreate them. Columns are auto-detected (name, URL, type, interval).
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <label className="flex cursor-pointer flex-col items-center justify-center gap-2 rounded-lg border border-dashed border-muted-foreground/30 p-8 text-center hover:border-primary">
            <FileUp className="h-8 w-8 text-muted-foreground" />
            <span className="text-sm font-medium">{fileName || 'Choose a CSV file'}</span>
            <span className="text-xs text-muted-foreground">UptimeRobot, Pingdom, or any CSV with name/URL/type columns</span>
            <input
              type="file"
              accept=".csv,text/csv"
              className="hidden"
              onChange={(e) => { const f = e.target.files?.[0]; if (f) handleFile(f) }}
            />
          </label>

          {parsed.length > 0 && !result && (
            <>
              <div className="flex items-center justify-between">
                <p className="text-sm text-muted-foreground">
                  {parsed.length} monitor{parsed.length === 1 ? '' : 's'} found
                  {source !== 'generic' && <> · detected <Badge variant="secondary">{source}</Badge></>}
                </p>
                {createBlock ? (
                  <BlockedActionButton>
                    <Upload className="mr-2 h-4 w-4" />
                    Import {parsed.length}
                  </BlockedActionButton>
                ) : (
                  <Button onClick={() => importMutation.mutate()} disabled={parsed.length === 0 || importMutation.isPending}>
                    <Upload className="mr-2 h-4 w-4" />
                    {importMutation.isPending ? 'Importing…' : `Import ${parsed.length}`}
                  </Button>
                )}
              </div>
              <div className="max-h-80 overflow-auto rounded-md border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead>URL</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead>Interval</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {parsed.slice(0, 100).map((p, i) => (
                      <TableRow key={i}>
                        <TableCell className="max-w-[160px] truncate">{p.name || <span className="text-muted-foreground">—</span>}</TableCell>
                        <TableCell className="max-w-[220px] truncate font-mono text-xs">{p.url}</TableCell>
                        <TableCell>{p.type || 'http'}</TableCell>
                        <TableCell>{p.interval ? `${p.interval}s` : '—'}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                {parsed.length > 100 && <p className="p-2 text-center text-xs text-muted-foreground">+ {parsed.length - 100} more</p>}
              </div>
            </>
          )}

          {result && (
            <div className="space-y-3">
              <div className="flex gap-2">
                <Badge className="bg-status-up">{result.created} created</Badge>
                {result.skipped > 0 && <Badge variant="secondary">{result.skipped} skipped</Badge>}
                {result.failed > 0 && <Badge variant="destructive">{result.failed} failed</Badge>}
              </div>
              {(result.skipped > 0 || result.failed > 0) && (
                <div className="max-h-60 overflow-auto rounded-md border">
                  <Table>
                    <TableBody>
                      {result.results.filter((r) => r.status !== 'created').map((r, i) => (
                        <TableRow key={i}>
                          <TableCell className="font-mono text-xs">{r.url}</TableCell>
                          <TableCell><Badge variant={r.status === 'failed' ? 'destructive' : 'secondary'}>{r.status}</Badge></TableCell>
                          <TableCell className="text-xs text-muted-foreground">{r.reason}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
              <div className="flex gap-2">
                <Button onClick={() => navigate({ to: '/dashboard/monitors' })}>View monitors</Button>
                <Button variant="outline" onClick={() => { setParsed([]); setResult(null); setFileName('') }}>Import another file</Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
